import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, ShieldAlert } from 'lucide-react';
import { settlementService } from '@/services/settlementService';
import { formatCurrency } from '@/lib/utils';
import { SettlementCalculationResult } from '@/engine/settlement_calculator';

interface SettlementApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  settlementId: string;
  cycleId: string;
  cycleCode: string;
  growerName: string;
  calculationResult: SettlementCalculationResult;
  criticalIssues: any[];
}

export const SettlementApprovalModal: React.FC<SettlementApprovalModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  settlementId,
  cycleId,
  cycleCode,
  growerName,
  calculationResult,
  criticalIssues,
}) => {
  const [notes, setNotes] = useState<string>('');
  const [adminOverride, setAdminOverride] = useState<boolean>(false);
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const hasCriticalIssues = criticalIssues && criticalIssues.length > 0;

  const handleApprove = async () => {
    if (hasCriticalIssues && (!adminOverride || !overrideReason.trim())) {
      setError('Unresolved critical reconciliation issues exist. Admin override with a mandatory justification reason is required to approve.');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const res = await settlementService.approveSettlement(
        settlementId,
        cycleId,
        notes.trim() || undefined,
        adminOverride,
        overrideReason.trim() || undefined
      );

      if (!res.success) {
        throw new Error(res.error || 'Failed to approve settlement');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred during settlement approval.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-6">
        <DialogHeader className="pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-indigo-900">
            <CheckCircle2 className="w-5 h-5 text-indigo-600" />
            <DialogTitle className="text-base font-bold">Approve Grower Settlement</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2 text-xs">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-md flex items-start gap-2 text-rose-700">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Cycle & Financial Overview Summary */}
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
            <div className="flex justify-between">
              <span className="text-slate-500">Production Cycle:</span>
              <span className="font-bold text-slate-800">{cycleCode}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Grower:</span>
              <span className="font-semibold text-slate-800">{growerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Gross Operating Profit:</span>
              <span className="font-mono font-bold text-slate-900">{formatCurrency(calculationResult.gross_profit)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-1">
              <span className="font-bold text-slate-700">Grower Net Payout:</span>
              <span className="font-mono font-bold text-indigo-700">{formatCurrency(calculationResult.grower_net_payout)}</span>
            </div>
          </div>

          {/* Critical Issues Check & Override Guard */}
          {hasCriticalIssues ? (
            <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-lg space-y-2.5">
              <div className="flex items-center gap-2 text-amber-900 font-bold">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>{criticalIssues.length} Unresolved Critical Reconciliation Issue(s)</span>
              </div>
              <p className="text-amber-800 text-[11px] leading-relaxed">
                This cycle has unverified financial or operational discrepancies in the Review Queue. Standard approval is blocked unless authorized by an Administrator.
              </p>
              <ul className="list-disc pl-4 space-y-1 text-amber-900 text-[11px]">
                {criticalIssues.map((issue) => (
                  <li key={issue.id}>
                    <span className="font-semibold">{issue.issue_type}:</span> {issue.description}
                  </li>
                ))}
              </ul>

              <div className="pt-2 border-t border-amber-200/80 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={adminOverride}
                    onChange={(e) => setAdminOverride(e.target.checked)}
                    className="mt-0.5 rounded border-amber-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="font-semibold text-amber-950">
                    I authorize an Administrator Override to approve this settlement despite open critical issues.
                  </span>
                </label>

                {adminOverride && (
                  <div>
                    <label className="block text-[11px] font-semibold text-amber-950 mb-1">
                      Mandatory Override Justification <span className="text-rose-600">*</span>
                    </label>
                    <textarea
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Explain why this settlement is approved prior to resolving critical issues..."
                      rows={2}
                      className="w-full px-2.5 py-1.5 rounded border border-amber-300 bg-white text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Zero unresolved critical reconciliation issues. Ready for supervisor approval.</span>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block font-semibold text-slate-700 mb-1">Approval Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add supervisor review notes or audit remarks..."
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-xs text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
          </div>
        </div>

        <DialogFooter className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading} className="text-xs">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleApprove}
            disabled={loading || (hasCriticalIssues && (!adminOverride || !overrideReason.trim()))}
            className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm"
          >
            {loading ? 'Approving...' : 'Confirm Approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
